import { CampaignPackage } from "@/hooks/useActiveCampaign";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import StockBadge from "./StockBadge";
import { Check, Crown, Loader2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

interface TrialSubscriptionCardProps {
  pkg: CampaignPackage;
  isAuthenticated: boolean;
  isPurchasing: boolean;
  currentSubscriptionPackageId: string | null;
  isUnlimitedUser: boolean;
  currentPath: string;
  onPurchase: () => void;
  remaining?: number | null;
}

const TrialSubscriptionCard = ({
  pkg,
  isAuthenticated,
  isPurchasing,
  currentSubscriptionPackageId,
  isUnlimitedUser,
  currentPath,
  onPurchase,
  remaining,
}: TrialSubscriptionCardProps) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const trialPrice = Number(pkg.price);
  const monthlyPrice = pkg.subscription_price_monthly;
  const trialDays = pkg.trial_days || 0;
  const monthlyCredits = pkg.subscription_credits_monthly || 0;
  const hasUnlimitedGrant = !!(pkg.grants_unlimited_days || pkg.unlimited_start_date);

  const isDirectSubscription = !trialDays || trialDays === 0;
  const isCurrentPlan = currentSubscriptionPackageId === pkg.id;
  const hasOtherSubscription = !!currentSubscriptionPackageId && !isCurrentPlan;
  const isSoldOut = remaining === 0;

  const handleChangePlan = async () => {
    setIsRedirecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-subscription");
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Error opening customer portal:", err);
      setIsRedirecting(false);
    }
  };

  return (
    <Card className={`p-6 relative overflow-hidden border-primary/30 bg-gradient-to-br from-card to-primary/5 ${isCurrentPlan ? 'ring-2 ring-primary' : ''} ${isSoldOut && !isCurrentPlan ? 'opacity-60' : ''}`}>
      {/* Badges */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <StockBadge remaining={remaining} />
        <Badge className="bg-primary text-primary-foreground text-xs">
          <RefreshCw className="h-3 w-3 mr-1" />
          Subscription
        </Badge>
        {isCurrentPlan && (
          <Badge variant="secondary" className="text-xs">
            Your Current Plan
          </Badge>
        )}
        {!isCurrentPlan && !isDirectSubscription && trialPrice === 0 && (
          <Badge variant="secondary" className="text-xs">
            Free Trial
          </Badge>
        )}
        {!isCurrentPlan && !isDirectSubscription && trialPrice > 0 && monthlyPrice && trialPrice < monthlyPrice && (
          <Badge variant="outline" className="text-xs">
            Try for £{trialPrice}
          </Badge>
        )}
        <Badge variant="outline" className="text-xs">
          Cancel anytime
        </Badge>
      </div>

      <h3 className="text-xl font-bold text-foreground mb-1">{pkg.name}</h3>
      <p className="text-muted-foreground text-sm mb-4 whitespace-pre-line">{pkg.description}</p>

      {/* Details */}
      <div className="space-y-2 mb-4">
        {isDirectSubscription ? (
          <>
            {monthlyCredits > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-success" />
                <span>{monthlyCredits} credits/month</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-success" />
              <span>Cancel anytime</span>
            </div>
          </>
        ) : (
          <>
            {hasUnlimitedGrant ? (
              <div className="flex items-center gap-2 text-sm">
                <Crown className="h-4 w-4 text-warning" />
                <span>Unlimited access for {trialDays} days</span>
              </div>
            ) : (
              pkg.credits_amount > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-success" />
                  <span>{pkg.credits_amount} credits during trial</span>
                </div>
              )
            )}
            {monthlyCredits > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-success" />
                <span>{monthlyCredits} credits/month after trial</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-success" />
              <span>Cancel anytime during trial</span>
            </div>
          </>
        )}
      </div>

      {/* Pricing */}
      <div className="bg-muted/50 rounded-md p-3 mb-4">
        {isDirectSubscription ? (
          <div>
            <span className="text-2xl font-bold text-foreground">
              £{monthlyPrice || trialPrice}
            </span>
            <span className="text-sm text-muted-foreground ml-1">/month</span>
          </div>
        ) : trialPrice === 0 ? (
          <div>
            <span className="text-2xl font-bold text-foreground">Free</span>
            <span className="text-sm text-muted-foreground ml-2">
              for {trialDays} days
            </span>
            {monthlyPrice && (
              <p className="text-sm text-muted-foreground mt-1">
                Then £{monthlyPrice}/month
              </p>
            )}
          </div>
        ) : (
          <div>
            <span className="text-2xl font-bold text-foreground">
              £{trialPrice}
            </span>
            <span className="text-sm text-muted-foreground ml-2">
              for your first month
            </span>
            {monthlyPrice && (
              <p className="text-sm text-muted-foreground mt-1">
                Then £{monthlyPrice}/month
              </p>
            )}
          </div>
        )}
      </div>

      {/* CTA */}
      <div>
        {!isAuthenticated ? (
          <Link to={`/auth?redirectTo=${encodeURIComponent(currentPath)}`} className="w-full">
            <Button variant="secondary" className="w-full">
              {isDirectSubscription ? "Sign In to Subscribe" : "Sign In to Start Trial"}
            </Button>
          </Link>
        ) : isCurrentPlan ? (
          <Button disabled variant="outline" className="w-full">
            <Check className="h-4 w-4 mr-2" />
            Your Current Plan
          </Button>
        ) : hasOtherSubscription ? (
          <Button
            onClick={handleChangePlan}
            disabled={isRedirecting}
            className="w-full"
            variant="secondary"
          >
            {isRedirecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Change to this Plan"
            )}
          </Button>
        ) : isSoldOut ? (
          <Button disabled variant="outline" className="w-full">
            Sold Out
          </Button>
        ) : isUnlimitedUser ? (
          <Button disabled variant="outline" className="w-full">
            Already Unlimited
          </Button>
        ) : (
          <Button
            onClick={onPurchase}
            disabled={isPurchasing}
            className="w-full"
            variant="secondary"
          >
            {isPurchasing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isDirectSubscription ? (
              `Subscribe — £${monthlyPrice || trialPrice}/month`
            ) : trialPrice === 0 ? (
              "Start Free Trial"
            ) : (
              `Start Trial — £${trialPrice}`
            )}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default TrialSubscriptionCard;
